import { useState, useEffect } from "react";
import { Box, Grid, Typography } from "@mui/material";
import dayjs from "dayjs";
import ReportLoader from "../../Reports/Loader";
import { colors } from "./source.styles";
import SourceHeader from "./SourceHeader";
import MetricsCards from "./MetricsCards";
import SourceDistribution from "./SourceDistribution";
import SourceQualityLeaderboard from "./SourceQualityLeaderboard";
import DetailedSourcePerformance from "./DetailedSourcePerformance";
import { getSourcePerformanceApi } from "../../../api/leads.api";
import Services from "../services";
import Conversions from "../conversion";
import Payments from "../payments";
import EmployeeReports from "../employees";
import Bookings from "../booking";

// Storage keys for persisting state
const STORAGE_KEYS = {
  ACTIVE_TAB: "reports_active_tab",
  DATE_RANGE: "reports_date_range",
};

// Get default date range (current month)
const getDefaultDateRange = () => {
  const now = dayjs();
  return {
    startDate: now.startOf("month").toDate(),
    endDate: now.endOf("month").toDate(),
  };
};

const SourceReports = () => {
  // Initialize state from localStorage
  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.ACTIVE_TAB);
    return saved || "sources";
  });

  const [dateRange, setDateRange] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.DATE_RANGE);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        startDate: new Date(parsed.startDate),
        endDate: new Date(parsed.endDate),
      };
    }
    return getDefaultDateRange();
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDetailedView, setIsDetailedView] = useState(false);

  // Persist activeTab to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.ACTIVE_TAB, activeTab);
  }, [activeTab]);

  // Persist dateRange to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.DATE_RANGE, JSON.stringify(dateRange));
  }, [dateRange]);

  const handleDateChange = (newRange) => {
    setDateRange(newRange);
  };

  // State for all data
  const [metricsData, setMetricsData] = useState(null);
  const [distributionData, setDistributionData] = useState([]);
  const [qualityData, setQualityData] = useState([]);
  const [performanceData, setPerformanceData] = useState([]);

  // Fetch source report data
  useEffect(() => {
    if (activeTab === "sources") {
      fetchSourceData();
    }
  }, [dateRange, activeTab]);

  const fetchSourceData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (dateRange.startDate)
        params.startDate = dateRange.startDate.toISOString();
      if (dateRange.endDate) params.endDate = dateRange.endDate.toISOString();

      // Fetch optimized source performance data
      const response = await getSourcePerformanceApi(params);
      const data = response.data?.data;

      if (!data) {
        setMetricsData({ topSource: "N/A", totalSpend: 0, avgCost: 0, roi: 0 });
        setDistributionData([]);
        setQualityData([]);
        setPerformanceData([]);
        return;
      }

      // Set Metrics Data from API
      setMetricsData({
        topSource: data.summary.topSource || "N/A",
        totalLeads: data.summary.totalLeads || 0,
        totalSources: data.summary.totalSources || 0,
        totalRevenue: data.summary.totalRevenue || 0,
        topSourceGrowth: data.summary.topSourceGrowth || 0,
      });

      // Set Distribution Data
      setDistributionData(
        data.sources.slice(0, 5).map((s) => ({
          name: s.source,
          value: s.totalLeads,
        })),
      );

      // Set Quality Data - Sort by quality score descending and take top 5
      setQualityData(
        [...data.sources]
          .sort((a, b) => b.qualityScore - a.qualityScore)
          .slice(0, 5)
          .map((s) => ({
            name: s.source,
            score: s.qualityScore,
            status: s.qualityLabel,
          })),
      );

      // Set Performance Data - Include all detailed stage breakdown data
      setPerformanceData(
        data.sources.map((s) => ({
          source: s.source,
          totalLeads: s.totalLeads,
          conversionRate: s.conversionRate,
          trend7Days: s.trend7Days,
          revenue: s.revenue,
          qualityScore: s.qualityScore,
          qualityLabel: s.qualityLabel,
          // Stage breakdown
          newLeads: s.newLeads,
          coldLeads: s.coldLeads,
          hotLeads: s.hotLeads,
          warmLeads: s.warmLeads,
          qualifiedLeads: s.qualifiedLeads,
          needAnalysisLeads: s.needAnalysisLeads,
          bookedLeads: s.bookedLeads,
          postConsultLeads: s.postConsultLeads,
          proposalSharedLeads: s.proposalSharedLeads,
          negotiationLeads: s.negotiationLeads,
          treatmentBookedLeads: s.treatmentBookedLeads,
          wonLeads: s.wonLeads,
        })),
      );
    } catch (err) {
      console.error("Error fetching source data:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          "Failed to load source reports",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    console.log("Exporting report...");
  };

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setIsDetailedView(false); // Reset detailed view when changing tabs
  };

  // Loading state (only for sources tab)
  if (loading && activeTab === "sources") {
    return <ReportLoader />;
  }

  // Error state (only for sources tab)
  if (error && activeTab === "sources") {
    return (
      <Box
        sx={{
          height: "100vh",
          bgcolor: colors.bgPrimary,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box sx={{ textAlign: "center", maxWidth: 400, p: 3 }}>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 600,
              color: colors.textPrimary,
              mb: 1,
            }}
          >
            Failed to Load Reports
          </Typography>
          <Typography sx={{ fontSize: 14, color: colors.textSecondary, mb: 3 }}>
            {error}
          </Typography>
          <Box
            component="button"
            onClick={fetchSourceData}
            sx={{
              px: 3,
              py: 1,
              fontSize: 13,
              fontWeight: 600,
              color: "#FFF",
              bgcolor: colors.primary,
              border: "none",
              borderRadius: 2,
              cursor: "pointer",
              "&:hover": {
                bgcolor: colors.primaryDark,
              },
            }}
          >
            Retry
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100vh",
        bgcolor: colors.bgPrimary,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      {!isDetailedView && (
        <SourceHeader
          activeTab={activeTab}
          onTabChange={handleTabChange}
          onExport={handleExport}
          dateRange={dateRange}
          onDateChange={handleDateChange}
        />
      )}

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: isDetailedView ? 0 : 4,
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            bgcolor: "#F8FAFC",
          },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "#E2E8F0",
            borderRadius: "4px",
            "&:hover": {
              bgcolor: "#CBD5E1",
            },
          },
        }}
      >
        {activeTab === "services" ? (
          <Services dateRange={dateRange} onDateChange={handleDateChange} />
        ) : activeTab === "conversions" ? (
          <Conversions dateRange={dateRange} onDateChange={handleDateChange} onDetailedViewChange={setIsDetailedView} />
        ) : activeTab === "payments" ? (
          <Payments dateRange={dateRange} />
        ) : activeTab === "employee" ? (
          <EmployeeReports dateRange={dateRange} onDetailedViewChange={setIsDetailedView} />
        ) : activeTab === "bookings" ? (
          <Bookings dateRange={dateRange} />
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {/* Metrics Cards */}
            {metricsData && <MetricsCards data={metricsData} />}

            {/* Two Column Layout */}
            <Grid
              container
              spacing={3}
              sx={{ "& > .MuiGrid-root": { display: "flex" } }}
            >
              <Grid size={{ xs: 12, md: 6 }}>
                <SourceDistribution data={distributionData} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <SourceQualityLeaderboard data={qualityData} />
              </Grid>
            </Grid>

            {/* Detailed Performance Table */}
            <DetailedSourcePerformance data={performanceData} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SourceReports;
